import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Minus, Plus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal } = useCart();
  const [loading, setLoading] = useState(false);
  const [paypalEnabled, setPaypalEnabled] = useState(false);

  useEffect(() => {
    api.get("/payments/methods").then((res) => setPaypalEnabled(!!res.data.paypal)).catch(() => {});
  }, []);

  const checkout = async () => {
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product.id, size: i.size, quantity: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Checkout indisponible. Réessayez.");
      setLoading(false);
    }
  };

  const paypalCheckout = async () => {
    setLoading(true);
    try {
      const res = await api.post("/paypal/create-order", {
        items: items.map((i) => ({ product_id: i.product.id, size: i.size, quantity: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.approval_url;
    } catch {
      toast.error("PayPal indisponible. Réessayez.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-white/10 bg-coal"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <h2 className="font-display text-lg font-bold uppercase tracking-wider">
                Panier <span className="text-ember">({items.length})</span>
              </h2>
              <button
                data-testid="cart-close-button"
                onClick={() => setIsOpen(false)}
                className="flex h-10 w-10 items-center justify-center border border-white/10 text-zinc-400 transition-colors duration-300 hover:border-ember hover:text-ember"
                aria-label="Close cart"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <p className="font-serif text-2xl italic text-zinc-500">Le panier est vide.</p>
                  <button
                    data-testid="cart-continue-shopping"
                    onClick={() => setIsOpen(false)}
                    className="font-mono text-[11px] uppercase tracking-[0.25em] text-ember transition-colors duration-300 hover:text-emberhover"
                  >
                    Continuer vos achats
                  </button>
                </div>
              ) : (
                <ul className="space-y-6">
                  {items.map((item) => (
                    <motion.li
                      layout
                      key={item.key}
                      data-testid={`cart-item-${item.key}`}
                      className="flex gap-4 border-b border-white/5 pb-6"
                    >
                      <div className="h-24 w-20 shrink-0 overflow-hidden bg-ink">
                        <img src={item.product.image} alt={item.product.name} className="h-full w-full object-cover" />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-display text-sm font-semibold uppercase leading-tight">
                              {item.product.name}
                            </p>
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                              Taille {item.size}
                            </p>
                          </div>
                          <button
                            data-testid={`cart-remove-${item.key}`}
                            onClick={() => removeItem(item.key)}
                            className="text-zinc-600 transition-colors duration-300 hover:text-ember"
                            aria-label="Remove item"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-auto flex items-center justify-between pt-3">
                          <div className="flex items-center border border-white/10">
                            <button
                              data-testid={`cart-qty-minus-${item.key}`}
                              onClick={() => updateQty(item.key, -1)}
                              className="flex h-8 w-8 items-center justify-center text-zinc-400 hover:text-ember"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-8 text-center font-mono text-xs">{item.qty}</span>
                            <button
                              data-testid={`cart-qty-plus-${item.key}`}
                              onClick={() => updateQty(item.key, 1)}
                              className="flex h-8 w-8 items-center justify-center text-zinc-400 hover:text-ember"
                              aria-label="Increase quantity"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <p className="font-mono text-sm text-zinc-200">
                            €{(item.product.price * item.qty).toFixed(0)}
                          </p>
                        </div>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 px-6 py-6">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Sous-total</span>
                  <span data-testid="cart-subtotal" className="font-display text-xl font-bold">
                    €{subtotal.toFixed(0)}
                  </span>
                </div>
                <p className="mt-2 font-mono text-[10px] tracking-wider text-zinc-600">
                  Taxes et livraison calculées à la commande.
                </p>
                <button
                  data-testid="cart-checkout-button"
                  onClick={checkout}
                  disabled={loading}
                  className="group mt-6 flex h-14 w-full items-center justify-center gap-3 bg-ember font-mono text-[11px] uppercase tracking-[0.3em] text-white transition-colors duration-300 hover:bg-emberhover disabled:opacity-60"
                >
                  {loading ? "Redirection..." : "Checkout"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                {paypalEnabled && (
                  <button
                    data-testid="paypal-checkout-button"
                    onClick={paypalCheckout}
                    disabled={loading}
                    className="mt-3 flex h-14 w-full items-center justify-center gap-3 border border-white/20 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-100 transition-colors duration-300 hover:border-white disabled:opacity-60"
                  >
                    Payer avec PayPal
                  </button>
                )}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
